import { FC, ReactNode, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { ECHARTS_THEME } from "../../echartsTheme";
import { Box, Typography } from "@mui/material";

import CoreCard from "../../../ui/core/CoreCard";
import UsageDownloadCsv from "./UsageDownloadCsv";
import { downloadLocalCsv } from "./downloadLocalCsv";

export interface BarDatum {
  name: string;
  value: number;
  color?: string;
}

interface BarChartCardProps {
  title: string;
  bars: BarDatum[];
  formatValue?: (value: number) => string;
  csvFilename: string;
  csvValueHeader?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  controls?: ReactNode;
}

const BAR_COLOR = "#343d50";
const MAX_LABEL_CHARS = 22;

const defaultFormat = (value: number): string => `${value}`;

const truncate = (label: string): string =>
  label.length <= MAX_LABEL_CHARS
    ? label
    : `${label.slice(0, MAX_LABEL_CHARS - 1)}…`;

const buildOption = (
  bars: BarDatum[],
  formatValue: (value: number) => string,
): EChartsOption => ({
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "shadow" },
    appendTo: "body",
    formatter: (params: unknown) => {
      const items = (Array.isArray(params) ? params : [params]) as {
        axisValue?: string;
        value?: number;
        marker?: string;
      }[];
      if (!items.length) return "";
      const item = items[0];
      return `${item.marker ?? ""} ${item.axisValue ?? ""}: <b>${formatValue(
        Number(item.value ?? 0),
      )}</b>`;
    },
  },
  grid: { left: 8, right: 48, top: 8, bottom: 8, containLabel: true },
  xAxis: {
    type: "value",
    minInterval: 1,
    splitLine: { lineStyle: { type: "dashed", color: "#E0E3E9" } },
    axisLabel: { color: "#626b7f" },
  },
  yAxis: {
    type: "category",
    data: bars.map((b) => b.name),
    inverse: true,
    axisLabel: {
      color: "#626b7f",
      width: 140,
      overflow: "truncate",
      formatter: (value: string) => truncate(value),
    },
    axisLine: { lineStyle: { color: "#D1D7E0" } },
    axisTick: { show: false },
  },
  series: [
    {
      type: "bar",
      data: bars.map((b) => ({
        value: b.value,
        itemStyle: { color: b.color ?? BAR_COLOR, borderRadius: [0, 4, 4, 0] },
      })),
      barMaxWidth: 22,
      label: {
        show: true,
        position: "right",
        color: "#626b7f",
        formatter: (params) =>
          formatValue(Number((params as { value?: number }).value ?? 0)),
      },
    },
  ],
});

const BarChartCard: FC<BarChartCardProps> = ({
  title,
  bars,
  formatValue = defaultFormat,
  csvFilename,
  csvValueHeader = "Value",
  emptyMessage,
  isLoading,
  controls,
}) => {
  const hasData = bars.some((b) => b.value > 0);
  const option = useMemo(
    () => buildOption(bars, formatValue),
    [bars, formatValue],
  );
  const csvData = useMemo(() => bars.map((b) => [b.name, b.value]), [bars]);

  const titleBar = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        marginBottom: 1.5,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          flexShrink: 0,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      {controls}
      <Box sx={{ flex: 1 }} />
      {hasData ? (
        <UsageDownloadCsv
          onDownload={() =>
            downloadLocalCsv(["Category", csvValueHeader], csvData, csvFilename)
          }
          disabled={csvData.length === 0}
        />
      ) : null}
    </Box>
  );

  return (
    <CoreCard
      isLoading={isLoading}
      sx={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      {titleBar}
      {hasData ? (
        <Box sx={{ flex: 1, minHeight: 0, overflowX: "hidden", overflowY: "auto" }}>
          <ReactECharts
            option={option}
            theme={ECHARTS_THEME}
            style={{ height: Math.max(bars.length * 36, 120), width: "100%" }}
            notMerge
            lazyUpdate
          />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {emptyMessage ?? "No data available"}
          </Typography>
        </Box>
      )}
    </CoreCard>
  );
};

export default BarChartCard;
